import { UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import { IHeartRate } from 'fitbeat.models';
import React, { FunctionComponent, useEffect } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import CheckMarkSolid from '../../assets/icons/check-circle-solid.svg';
import { TranslateProp } from '../../common-types';
import { Button, ButtonTypes } from '../../components';
import connectedAppsManager from '../../ConnectedApps';
import ConnectedWorkoutApp, { SupportedConnectedApp } from '../../ConnectedApps/connected-workout-app';
import { currentActiveOrCheckedInWorkoutSelector } from '../../SummaryDashboard/selectors';
import Theme, { ThemeType } from '../../theme';
import { AppStateFocusedEvents, ExclamationIcon } from '../../ui';
import { UserDataConsumer } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import {
  startWatchAppWorkoutPressed,
} from '../heartRateActions';
import { IAppStore } from '../reducer';
import { showMessage } from '../../ui/components/Toast';

interface IProps {
  latestConnectedAppHR: IHeartRate | null;
  themeType: ThemeType;
  user: {
    userProfile: UserProfile;
  };
  t: TranslateProp;
  showFaq(): void;
}

interface IMappedState {
  currentActiveOrCheckedInWorkout?: UserWorkoutSession,
  waitingForWatchHRData: boolean;
  failedToReceiveHRFromConnectedApp: boolean;
  failedToReceiveHRFromConnectedAppError: string | null;
}

interface IMappedDispatch {
  startWatchAppWorkout(userWorkoutSession: UserWorkoutSession, userProfile: UserProfile): void;
}

const styles = StyleSheet.create({
  currentConnectedHrWrapper: {
    alignItems: 'center',
    borderBottomWidth: 1.5,
    borderBottomColor: Theme.GREY_DARK_MEDIUM,
  },
  buttonContainer: {
    alignItems: 'stretch',
    padding: defaultScale(10),
  },
  lightBackgroundBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Theme.GREY_MEDIUM_LIGHT,
  },
  currentConnectedHrInner: {
    flexDirection: 'row',
    marginHorizontal: defaultScale(22),
  },
  currentConnectedHrReading: {
    ...Theme.textStyle.heading,
    color: Theme.GREEN,
    marginVertical: defaultScale(8),
    alignSelf: 'center',
    textAlign: 'center',
  },
  errorContainer: {
    alignItems: 'center',
    paddingBottom: defaultScale(10),
  },
  errorIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: defaultScale(2),
  },
  errorText: {
    ...Theme.textStyle.bodyCopyBold,
    marginLeft: defaultScale(5),
  },
  errorTextDetail: {
    ...Theme.textStyle.mediumItalic,
    marginLeft: defaultScale(5),
  },
  seeTroubleShooting: {
    ...Theme.textStyle.mediumItalic,
    textDecorationLine: 'underline',
  },
});

// todo: currently only Apple Health is available as an HR monitor
//  changes are needed here to get another connected app for use here
const CurrentConnectedAppHeartRateBanner: FunctionComponent<IProps & IMappedState & IMappedDispatch> = (
  {
    t,
    latestConnectedAppHR,
    currentActiveOrCheckedInWorkout,
    startWatchAppWorkout,
    user,
    waitingForWatchHRData,
    themeType,
    failedToReceiveHRFromConnectedApp,
    failedToReceiveHRFromConnectedAppError,
    showFaq,
  },
) => {
  const [canUseConnectedAppForHR, setCanUseConnectedAppForHR] = React.useState<boolean>(false);
  const [shouldRequestAuthForInstalledApp, setShouldRequestAuthForInstalledApp] = React.useState<boolean>(false);
  const [authorizationError, setAuthorizationError] = React.useState<string | null>(null);
  useEffect(() => {
    // TODO: consider changing these values to computed values on each connected app
    connectedAppsManager.canUseConnectedAppForHeartRate()
      .then((canUseForHR) => setCanUseConnectedAppForHR(canUseForHR))
      .catch(() => setCanUseConnectedAppForHR(false));
    connectedAppsManager.shouldRequestAuthorizationForInstalledApp()
      .then((shouldRequest) => setShouldRequestAuthForInstalledApp(shouldRequest))
      .catch(() => setShouldRequestAuthForInstalledApp(false));
  });

  const hrSourceMap: Record<SupportedConnectedApp, string> = {
    'Apple Health': t('heartRateDevice:appleWatch'),
  };

  const hrConnectingMap: Record<SupportedConnectedApp, string> = {
    'Apple Health': t('heartRateDevice:appleWatchConnecting'),
  };

  if (latestConnectedAppHR?.source && latestConnectedAppHR?.heartRate) {
    return (
      <View style={[styles.currentConnectedHrWrapper, themeType === ThemeType.light && styles.lightBackgroundBorder]}>
        <View style={styles.currentConnectedHrInner}>
          <CheckMarkSolid
            width={defaultScale(32)}
            height={defaultScale(40)}
            fill={Theme.GREEN}
          />
          <Text style={styles.currentConnectedHrReading}>
            {t('heartRateDevice:connectedAppHrReading', {
              source: hrSourceMap[latestConnectedAppHR.source],
              bpm: latestConnectedAppHR.heartRate.toString(),
            })}
          </Text>
        </View>
      </View>
    );
  }

  if (canUseConnectedAppForHR && currentActiveOrCheckedInWorkout && waitingForWatchHRData) {
    return (
      <View style={[styles.currentConnectedHrWrapper, themeType === ThemeType.light && styles.lightBackgroundBorder]}>
        <View style={styles.currentConnectedHrInner}>
          <Text style={[styles.currentConnectedHrReading, { color: Theme.PRIMARY, marginRight: defaultScale(5) }]}>
            {hrConnectingMap['Apple Health']}
          </Text>
          <ActivityIndicator color={Theme.PRIMARY} size={defaultScale(28)} />
        </View>
      </View>
    );
  }

  const textColor = themeType === ThemeType.light ? Theme.GREY_DARK : Theme.WHITE;
  if (canUseConnectedAppForHR && currentActiveOrCheckedInWorkout) {
    return (
      <View style={[
        styles.currentConnectedHrWrapper,
        styles.buttonContainer,
        themeType === ThemeType.light && styles.lightBackgroundBorder,
      ]}
      >
        {failedToReceiveHRFromConnectedApp && (
          <TouchableOpacity onPress={showFaq} style={styles.errorContainer}>
            <View style={styles.errorIconContainer}>
              <ExclamationIcon
                color={textColor}
                borderColor={textColor}
                size={defaultScale(25)}
                fontSize={defaultScale(16)}
              />
              <Text style={[styles.errorText, { color: textColor }]}>{t('heartRateDevice:errorAppleWatchApp')}</Text>
            </View>
            {failedToReceiveHRFromConnectedAppError && (
              <Text style={[styles.errorTextDetail, { color: textColor }]}>
                {failedToReceiveHRFromConnectedAppError}
              </Text>
            )}
          </TouchableOpacity>
        )}
        <Button
          title={t(
            failedToReceiveHRFromConnectedApp
              ? 'heartRateDevice:retryAppleWatchApp'
              : 'heartRateDevice:startAppleWatchApp',
          )}
          onPress={() => startWatchAppWorkout(currentActiveOrCheckedInWorkout, user.userProfile)}
          fontColor={Theme.PRIMARY}
          buttonType={ButtonTypes.OUTLINE}
        />
        <Button
          title={t('heartRateDevice:errorAppleWatchSeeTroubleshooting')}
          onPress={showFaq}
          fontColor={Theme.PRIMARY}
          buttonType={ButtonTypes.TRANSPARENT}
        />
      </View>
    );
  }

  const startWatchWorkoutIfPermissionsGranted = async () => {
    const canUseAsHR = await connectedAppsManager.canUseConnectedAppForHeartRate();
    setCanUseConnectedAppForHR(canUseAsHR);
    if (canUseAsHR && currentActiveOrCheckedInWorkout) {
      startWatchAppWorkout(currentActiveOrCheckedInWorkout, user.userProfile);
    }
  };

  const requestAppPermission = async (connectedApp: ConnectedWorkoutApp) => {
    const { success, errorDisplayMessage } = await connectedApp.requestAuthorization();
    if (!success && errorDisplayMessage) {
      setAuthorizationError(errorDisplayMessage);
    } else {
      setAuthorizationError(null);
      await startWatchWorkoutIfPermissionsGranted();
    }
  };

  if (shouldRequestAuthForInstalledApp && currentActiveOrCheckedInWorkout) {
    return (
      <View style={[
        styles.currentConnectedHrWrapper,
        styles.buttonContainer,
        themeType === ThemeType.light && styles.lightBackgroundBorder]}
      >
        <AppStateFocusedEvents onScreenFocus={startWatchWorkoutIfPermissionsGranted} />
        {authorizationError && (
          <View>
            <View style={styles.errorIconContainer}>
              <ExclamationIcon
                color={textColor}
                borderColor={textColor}
                size={defaultScale(25)}
                fontSize={defaultScale(16)}
              />
              <Text style={[styles.errorText, { color: textColor }]}>{t('heartRateDevice:permissionRequired')}</Text>
            </View>
            <Text style={[styles.errorTextDetail, { color: textColor }]}>{authorizationError}</Text>
            <Text style={[styles.errorTextDetail, { color: textColor }]} />
          </View>
        )}
        {connectedAppsManager.connectedApps.map((connectedApp) => (
          <Button
            title={
              connectedApp.name === 'Apple Health'
                ? t('heartRateDevice:addAppleWatchPermission')
                : t('heartRateDevice:addConnectedAppPermission', { appName: connectedApp.name })
            }
            onPress={() => requestAppPermission(connectedApp)}
            fontColor={Theme.PRIMARY}
            buttonType={ButtonTypes.OUTLINE}
            isLoading={waitingForWatchHRData}
            key={connectedApp.name}
          />
        ))}
      </View>
    );
  }

  return <></>;
};

const mapStateToProps = (state: IAppStore): IMappedState => ({
  currentActiveOrCheckedInWorkout: currentActiveOrCheckedInWorkoutSelector(state),
  waitingForWatchHRData: state.heartRateMonitor.waitingForWatchHRData,
  failedToReceiveHRFromConnectedApp: state.heartRateMonitor.failedToReceiveHRFromConnectedApp,
  failedToReceiveHRFromConnectedAppError: state.heartRateMonitor.failedToReceiveHRFromConnectedAppError,
});

const mapDispatchToProps = (dispatch): IMappedDispatch => ({
  startWatchAppWorkout: (userWorkoutSession: UserWorkoutSession, userProfile: UserProfile) => dispatch(
    startWatchAppWorkoutPressed(userWorkoutSession, userProfile),
  ),
});

export default UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(CurrentConnectedAppHeartRateBanner));
